// Google Analytics Service for MyPet Platform
// Handles all analytics tracking and event management

declare global {
  interface Window {
    gtag: (...args: any[]) => void
    dataLayer: any[]
  }
}

export interface AnalyticsEvent {
  action: string
  category: string
  label?: string
  value?: number
}

export interface PageViewEvent {
  page_title: string
  page_location: string
  page_path: string
}

export interface ConversionEvent {
  event_category: string
  event_label: string
  value?: number
}


class AnalyticsService {
  private isInitialized = false
  private readonly GA_ID = 'G-F56JWS9NRV'
  private analyticsAllowed = false

  constructor() {
    this.initialize()
  }

  private initialize() {
    if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
      this.isInitialized = true
    }
  }

  private ensureInitialized() {
    if (!this.isInitialized && typeof window !== 'undefined') {
      // Wait for gtag to be available
      const checkGtag = () => {
        if (typeof window.gtag === 'function') {
          this.isInitialized = true
        } else {
          setTimeout(checkGtag, 100)
        }
      }
      checkGtag()
    }
  }

  setConsent(allowed: boolean) {
    this.analyticsAllowed = allowed
  }

  // Track page views
  trackPageView(pageData: PageViewEvent) {
    if (!this.analyticsAllowed) return
    this.ensureInitialized()
    
    if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
      window.gtag('config', this.GA_ID, {
        page_title: pageData.page_title,
        page_location: pageData.page_location,
        page_path: pageData.page_path,
      })
    }
  }

  // Track custom events
  trackEvent(eventData: AnalyticsEvent) {
    if (!this.analyticsAllowed) return
    this.ensureInitialized()
    
    if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
      window.gtag('event', eventData.action, {
        event_category: eventData.category,
        event_label: eventData.label,
        value: eventData.value,
      })
    }
  }

  // Track conversions
  trackConversion(conversionData: ConversionEvent) {
    if (!this.analyticsAllowed) return
    this.ensureInitialized()
    
    if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
      window.gtag('event', 'conversion', {
        send_to: this.GA_ID,
        event_category: conversionData.event_category,
        event_label: conversionData.event_label,
        value: conversionData.value,
      })
    }
  }

  // Track user interactions
  trackUserInteraction(action: string, category: string, label?: string, value?: number) {
    this.trackEvent({
      action,
      category,
      label,
      value,
    })
  }

  // Track search queries
  trackSearch(query: string, resultsCount?: number) {
    this.trackEvent({
      action: 'search',
      category: 'engagement',
      label: query,
      value: resultsCount,
    })
  }

  // Track business profile views
  trackBusinessView(_businessId: string, businessName: string) {
    this.trackEvent({
      action: 'view_business',
      category: 'business',
      label: businessName,
    })
  }

  // Track adoption listing views
  trackAdoptionView(_adoptionId: string, petName: string) {
    this.trackEvent({
      action: 'view_adoption',
      category: 'adoption',
      label: petName,
    })
  }

  // Track form submissions
  trackFormSubmission(formType: string, success: boolean = true) {
    this.trackEvent({
      action: 'form_submit',
      category: 'engagement',
      label: formType,
      value: success ? 1 : 0,
    })
  }

  // Track user registrations
  trackRegistration(userType: 'consumer' | 'business', success: boolean = true) {
    this.trackConversion({
      event_category: 'registration',
      event_label: userType,
      value: success ? 1 : 0,
    })
  }

  // Track business listing creation
  trackBusinessListingCreation(success: boolean = true) {
    this.trackConversion({
      event_category: 'business_listing',
      event_label: 'creation',
      value: success ? 1 : 0,
    })
  }

  // Track contact form submissions
  trackContactForm(contactType: string, success: boolean = true) {
    this.trackEvent({
      action: 'contact_form',
      category: 'engagement',
      label: contactType,
      value: success ? 1 : 0,
    })
  }

  // Track button clicks
  trackButtonClick(buttonName: string, location: string) {
    this.trackEvent({
      action: 'click',
      category: 'ui_interaction',
      label: `${buttonName}_${location}`,
    })
  }

  // Track navigation
  trackNavigation(from: string, to: string) {
    this.trackEvent({
      action: 'navigate',
      category: 'navigation',
      label: `${from}_to_${to}`,
    })
  }

  // Track errors
  trackError(errorType: string) {
    this.trackEvent({
      action: 'error',
      category: 'technical',
      label: errorType,
    })
  }

  // Track performance metrics
  trackPerformance(metric: string, value: number) {
    this.trackEvent({
      action: 'performance',
      category: 'technical',
      label: metric,
      value: Math.round(value),
    })
  }

  // Track user engagement
  trackEngagement(action: string, details?: string) {
    this.trackEvent({
      action,
      category: 'engagement',
      label: details,
    })
  }

  // Track business metrics
  trackBusinessMetric(metric: string, value: number) {
    this.trackEvent({
      action: 'business_metric',
      category: 'business',
      label: metric,
      value,
    })
  }

  // Track adoption metrics
  trackAdoptionMetric(metric: string, value: number) {
    this.trackEvent({
      action: 'adoption_metric',
      category: 'adoption',
      label: metric,
      value,
    })
  }

  // Set user properties
  setUserProperties(properties: Record<string, any>) {
    if (!this.analyticsAllowed) return
    this.ensureInitialized()
    
    if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
      window.gtag('config', this.GA_ID, {
        custom_map: properties,
      })
    }
  }

  // Set user ID for tracking
  setUserId(userId: string) {
    if (!this.analyticsAllowed) return
    this.ensureInitialized()
    
    if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
      window.gtag('config', this.GA_ID, {
        user_id: userId,
      })
    }
  }

  // Track e-commerce events (for future use)
  trackPurchase(transactionId: string, value: number, currency: string = 'ILS') {
    if (!this.analyticsAllowed) return
    this.ensureInitialized()
    
    if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
      window.gtag('event', 'purchase', {
        transaction_id: transactionId,
        value,
        currency,
      })
    }
  }

  // Track item views (for future use)
  trackItemView(itemId: string, itemName: string, category: string, price?: number) {
    if (!this.analyticsAllowed) return
    this.ensureInitialized()
    
    if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
      window.gtag('event', 'view_item', {
        item_id: itemId,
        item_name: itemName,
        item_category: category,
        value: price,
      })
    }
  }
}

// Create singleton instance
export const analyticsService = new AnalyticsService()
